import { apiClient } from './api';
import { Control, CreateControlRequest, CreateControlInstanceRequest, ControlFilters, ControlStats, ControlType, RequirementStatus, FormFieldStatus, UpdateRequirementStatusRequest, AddDocumentToControlResponse, UnlinkDocumentResponse, PreviousControl, RenewalProposal, ApplyRenewalRequest, ApplyRenewalResult } from '@/types';

export interface ControlListResponse {
  success: boolean;
  data: Control[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface ControlResponse {
  success: boolean;
  data: Control;
  message?: string;
}

export interface ControlStatsResponse {
  success: boolean;
  data: ControlStats;
}

export interface PredefinedControlTypesResponse {
  success: boolean;
  data: ControlType[];
}

export interface PredefinedControlTypeDetailResponse {
  success: boolean;
  data: ControlType;
}

export class ControlService {
  /**
   * Get all control types (unified with issuer)
   */
  static async getControlTypes(): Promise<ControlType[]> {
    console.log('🌐 ControlService.getControlTypes: Method called');
    try {
      const response = await apiClient.get<any>('/controls/types');
      console.log('Raw API response:', response);

      // Handle different possible response structures
      if (Array.isArray(response)) {
        return response;
      } else if (response && Array.isArray(response.data)) {
        return response.data;
      } else if (response && response.success && Array.isArray(response.data)) {
        return response.data;
      } else {
        console.error('Unexpected response structure:', response);
        throw new Error('Invalid response structure from server');
      }
    } catch (error) {
      console.error('Service error:', error);
      throw error;
    }
  }

  /**
   * Get specific control type by ID
   */
  static async getControlType(id: string): Promise<ControlType> {
    try {
      const response = await apiClient.get<any>(`/controls/types/${id}`);
      console.log('🔍 DETAILED getControlType response:', response);

      // Handle different possible response structures
      if (response && response.id) {
        console.log('✅ Using direct response (has ID)');
        return response;
      } else if (response && response.data && response.data.id) {
        console.log('✅ Using response.data (has ID)');
        return response.data;
      } else if (response && response.success && response.data && response.data.id) {
        console.log('✅ Using response.success.data (has ID)');
        return response.data;
      } else {
        console.error('❌ Unexpected response structure:', response);
        throw new Error('Invalid response structure from server');
      }
    } catch (error) {
      console.error('Service error:', error);
      throw error;
    }
  }

  /**
   * Get affarspartners available for the current user's selected agronodkonto
   */
  static async getAffarspartners(): Promise<{ id: string; name: string; orgnr: string }[]> {
    return apiClient.get<{ id: string; name: string; orgnr: string }[]>('/controls/affarspartners');
  }

  // Backward compatibility aliases
  static async getPredefinedControlTypes(): Promise<ControlType[]> {
    return this.getControlTypes();
  }

  static async getPredefinedControlType(id: string): Promise<ControlType> {
    return this.getControlType(id);
  }

  /**
   * Create a new control instance from predefined control type
   */
  static async createControlInstance(controlData: CreateControlInstanceRequest): Promise<Control> {
    try {
      console.log('Creating control instance with data:', controlData);
      const response = await apiClient.post<Control>('/controls/instances', controlData);
      console.log('Raw create control instance response:', response);
      
      // The ApiClient should return the control instance directly (extracts data from { success: true, data: control })
      if (response && (response.id || response.name)) {
        return response;
      } else {
        console.error('Unexpected create control instance response structure:', response);
        console.error('Response type:', typeof response);
        console.error('Response keys:', response ? Object.keys(response) : 'null');
        throw new Error('Invalid response structure from server');
      }
    } catch (error) {
      console.error('Create control instance service error:', error);
      throw error;
    }
  }

  /** The previous control of a type on the selected konto, or null. */
  static async getPreviousControl(
    controlTypeId: string,
    /** The date picked for the new control, so the line names the control creation will use. */
    scheduledDate?: string,
  ): Promise<PreviousControl | null> {
    const query = scheduledDate ? `?scheduledDate=${encodeURIComponent(scheduledDate)}` : '';
    const response = await apiClient.get<PreviousControl | null>(
      `/controls/types/${controlTypeId}/previous${query}`
    );
    return response ?? null;
  }

  /** What an untouched draft may fetch from its previous control, or null. */
  static async getRenewalProposal(controlId: string): Promise<RenewalProposal | null> {
    const response = await apiClient.get<RenewalProposal | null>(`/controls/${controlId}/renewal`);
    return response ?? null;
  }

  /** Write the farmer's selection from the previous control. Idempotent. */
  static async applyRenewal(
    controlId: string,
    body: ApplyRenewalRequest
  ): Promise<ApplyRenewalResult> {
    return apiClient.post<ApplyRenewalResult>(`/controls/${controlId}/renewal`, body);
  }

  /**
   * Create a new control (DEPRECATED - use createControlInstance instead)
   */
  static async createControl(controlData: CreateControlRequest): Promise<Control> {
    const response = await apiClient.post<ControlResponse>('/controls', controlData);
    return response.data;
  }

  /**
   * Get paginated list of controls with filtering
   */
  static async getControls(
    page: number = 1,
    limit: number = 20,
    filters: ControlFilters = {}
  ): Promise<{
    controls: Control[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    // Add filters
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.certificationType) params.append('certificationType', filters.certificationType);
    if (filters.controlType) params.append('controlType', filters.controlType);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    // apiClient.get() extracts 'data' property from { success: true, data: {...} }
    // The data contains { controls: [], pagination: {...} }
    const response = await apiClient.get<any>(`/controls?${params.toString()}`);
    console.log('getControls API response:', response);

    // Check if response has the expected structure
    if (response && response.controls && response.pagination) {
      // Response is already the extracted data object
      return {
        controls: response.controls || [],
        pagination: response.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    } else if (Array.isArray(response)) {
      // Fallback: if response is just an array
      return {
        controls: response,
        pagination: {
          page: 1,
          limit: 20,
          total: response.length,
          totalPages: 1,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    } else {
      console.error('Unexpected response structure:', response);
      return {
        controls: [],
        pagination: {
          page: 1,
          limit: 20,
          total: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }
  }

  /**
   * Get control by ID
   */
  static async getControlById(controlId: string): Promise<Control> {
    const response = await apiClient.get<any>(`/controls/${controlId}`);
    // apiClient already extracts data property, so response is the control object directly
    return response;
  }

  /**
   * Update control
   */
  static async updateControl(controlId: string, updates: Partial<Control>): Promise<Control> {
    const response = await apiClient.put<any>(`/controls/${controlId}`, updates);
    // apiClient already extracts data property, so response is the control object directly
    return response;
  }

  /**
   * Delete control
   */
  static async deleteControl(controlId: string): Promise<void> {
    await apiClient.delete(`/controls/${controlId}`);
  }

  /**
   * Manually add document to control with cross-control auto-linking.
   * When a document is added to a control, it automatically gets linked to other
   * controls within the same agronodkonto that have a requirement its tags satisfy.
   */
  static async addDocumentToControl(
    controlId: string,
    documentId: string,
    requirementId?: string,
    /** Attach to a form field instead of a requirement. */
    fieldId?: string
  ): Promise<AddDocumentToControlResponse> {
    const body: { documentId: string; requirementId?: string; fieldId?: string } = {
      documentId,
    };
    if (requirementId) {
      body.requirementId = requirementId;
    }
    if (fieldId) {
      body.fieldId = fieldId;
    }

    const response = await apiClient.post<AddDocumentToControlResponse>(
      `/controls/${controlId}/documents`,
      body
    );
    return response;
  }

  /**
   * Remove document from control (removes all document links)
   */
  static async removeDocumentFromControl(
    controlId: string,
    documentId: string
  ): Promise<void> {
    await apiClient.delete(`/controls/${controlId}/documents/${documentId}`);
  }

  /**
   * Unlink a document from a specific control only, without affecting other controls.
   * Used for cross-control document sharing - allows removing from one control
   * while keeping the document linked to other controls.
   */
  static async unlinkDocumentFromControl(
    controlId: string,
    documentId: string
  ): Promise<UnlinkDocumentResponse> {
    const response = await apiClient.delete<UnlinkDocumentResponse>(
      `/controls/${controlId}/documents/${documentId}/link`
    );
    return response;
  }

  /**
   * Get control analysis/completion status
   */
  static async getControlAnalysis(controlId: number): Promise<any> {
    const response = await apiClient.get<any>(`/controls/${controlId}/analysis`);
    return response;
  }

  /**
   * Get control dashboard statistics
   */
  static async getControlStats(): Promise<ControlStats> {
    const response = await apiClient.get<ControlStats>('/controls/dashboard/stats');
    return response;
  }

  /**
   * Update requirement verification status
   */
  static async updateRequirementStatus(
    controlId: string,
    requirementId: string,
    data: UpdateRequirementStatusRequest
  ): Promise<RequirementStatus> {
    const response = await apiClient.patch<RequirementStatus>(
      `/controls/${controlId}/requirements/${requirementId}/status`,
      data
    );
    return response;
  }

  /**
   * Update how an upload-enabled form field is handled (upload / checked on the
   * farm / not applicable) together with its comment.
   *
   * @param controlId The control the field is answered on
   * @param fieldId FormFieldDefinition.id — an admin-authored string, not a UUID
   * @param data Verification method and comment; an empty string removes the comment
   */
  static async updateFormFieldStatus(
    controlId: string,
    fieldId: string,
    data: UpdateRequirementStatusRequest
  ): Promise<FormFieldStatus> {
    const response = await apiClient.patch<FormFieldStatus>(
      `/controls/${controlId}/form-fields/${encodeURIComponent(fieldId)}/status`,
      data
    );
    return response;
  }

  /**
   * Get all requirement statuses for a control
   */
  static async getRequirementStatuses(controlId: string): Promise<RequirementStatus[]> {
    const response = await apiClient.get<RequirementStatus[]>(
      `/controls/${controlId}/requirements/statuses`
    );
    return response;
  }

  /**
   * Activate a farm-profile-filtered requirement on a specific control.
   * The farmer explicitly opts in to see and fulfill the requirement despite the filter.
   *
   * @param controlId - The control instance ID.
   * @param requirementId - The DocumentRequirement ID to activate.
   */
  static async activateRequirement(controlId: string, requirementId: string): Promise<void> {
    await apiClient.post<void>(
      `/controls/${controlId}/requirements/${requirementId}/activate`,
    );
  }

  /**
   * Deactivate a previously activated requirement on a specific control.
   * Returns normally on 204. Throws ApiError with statusCode 409 if the
   * requirement has linked documents — caller must surface an error dialog.
   *
   * @param controlId - The control instance ID.
   * @param requirementId - The DocumentRequirement ID to deactivate.
   */
  static async deactivateRequirement(controlId: string, requirementId: string): Promise<void> {
    await apiClient.delete<void>(
      `/controls/${controlId}/requirements/${requirementId}/activate`,
    );
  }

  /**
   * Activate a farm-profile-filtered form field on a specific control, so the
   * farmer can answer it despite the filter. Idempotent.
   *
   * @param controlId - The control instance ID.
   * @param fieldId - The FormFieldDefinition id to activate.
   */
  static async activateFormField(controlId: string, fieldId: string): Promise<void> {
    await apiClient.post<void>(`/controls/${controlId}/fields/${fieldId}/activate`);
  }

  /**
   * Deactivate a previously activated form field.
   * Throws ApiError with statusCode 409 if the field still holds an answer.
   *
   * @param controlId - The control instance ID.
   * @param fieldId - The FormFieldDefinition id to deactivate.
   */
  static async deactivateFormField(controlId: string, fieldId: string): Promise<void> {
    await apiClient.delete<void>(`/controls/${controlId}/fields/${fieldId}/activate`);
  }
}

export default ControlService;